import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { AppContainer, MainContentContainer, BodyContainer } from '../../resources/styling/appStyle';
import { Paging } from '../../components';
import {
  Grid,
  Chip,
  Checkbox,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Divider,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { format, parseISO, addYears } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import renewalService from '../../services/api/renewalService';
import { invoiceManagementAPI } from '../../services/api/urls';
import styled from 'styled-components';
import {
  sa,
  DataTable,
  Card,
  PillButton,
  SelectPill,
  SectionHeaderRow,
  SectionSubtitle,
  SectionStates,
} from '../../components/superAdminUi';

const PaginationWrapper = styled.div`
  width: 75%;
  margin: auto;
  text-align: center;
  padding: 20px 0;
`;

// Vertical rhythm between the header, stat cards, filter bar and table — matches the
// gap the redesigned Super Admin sections use.
const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
  font-family: ${sa.font.family};
`;

// Renewal summary tiles: responsive grid of design-system cards, each carrying its own
// accent underline (mirrors the stat cards on the Super Admin dashboard).
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled(Card)`
  border-bottom: 3px solid ${(p) => p.$accent};
  padding: 16px 18px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: ${sa.font.extrabold};
  line-height: 1.1;
  color: ${(p) => p.$color || sa.color.text};
`;

const StatLabel = styled.div`
  margin-top: 6px;
  font-size: 12px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.textGray};
`;

// Search + filter controls, laid out inside a single card above the table.
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 280px;
  min-width: 240px;
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};
  padding: 8px 12px;

  svg {
    color: ${sa.color.textGray};
    font-size: 20px;
  }
  input {
    flex: 1;
    border: none;
    outline: none;
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    background: transparent;
  }
`;

const FilterField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${sa.font.family};
  font-size: 13px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.textGray};
`;

// Clickable purple-header cell that toggles the same sort handlers as before; the arrow
// reflects the active column and direction.
const HeaderSort = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
`;

const SortArrow = styled.span`
  font-size: 11px;
  line-height: 1;
`;

const RowName = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
`;

const StackedCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: normal;
`;

const ActionCell = styled.div`
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${sa.radius.chip};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 12px;
  line-height: 1;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

// Presentational status palette for the pills — brand tokens where they exist, with two
// soft success/warning fills to round out the set.
const TONE = {
  neutral: { bg: sa.color.mist, fg: sa.color.textGray },
  success: { bg: '#dff5ea', fg: '#1f9d6b' },
  warning: { bg: '#fdecd2', fg: '#c77700' },
  danger: { bg: sa.color.groupBg, fg: sa.color.pink },
  trust: { bg: sa.color.groupBg, fg: sa.color.pink },
  school: { bg: sa.color.schoolBg, fg: sa.color.blue },
};

const STAT_ACCENT = {
  total: sa.color.teal,
  unprocessed: sa.color.accentAmber,
  invoiceGenerated: sa.color.accentGreen,
  pastDue: sa.color.pink,
  gracePeriod: sa.color.blue,
  totalValue: sa.color.purple,
};

function UpcomingRenewals(props) {
  const [loading, setLoading] = useState(true);
  const [renewals, setRenewals] = useState([]);
  const [filteredRenewals, setFilteredRenewals] = useState([]);
  const [pageOfItems, setPageOfItems] = useState([]);
  const [selectedRenewals, setSelectedRenewals] = useState([]);
  const [renewalWindowWeeks, setRenewalWindowWeeks] = useState(8);
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('renewalDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRenewal, setSelectedRenewal] = useState(null);
  const [processRenewalDialogOpen, setProcessRenewalDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [renewalWindowConfigDialogOpen, setRenewalWindowConfigDialogOpen] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    loadRenewals();
    loadRenewalWindowConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters when renewals or filter criteria change
  useEffect(() => {
    if (renewals.length > 0) {
      applyFilters(filterStatus, sortBy, sortOrder, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewals, filterStatus, sortBy, sortOrder, searchTerm]);

  const loadRenewals = async () => {
    setLoading(true);
    try {
      const response = await renewalService.GetUpcomingRenewals(renewalWindowWeeks);
      if (response.success && response.data) {
        setRenewals(response.data);
      } else {
        toast.error(response.message || 'Failed to load upcoming renewals');
        setRenewals([]);
        setFilteredRenewals([]);
        setPageOfItems([]);
      }
    } catch (error) {
      toast.error('Failed to load upcoming renewals');
      console.error('Error loading renewals:', error);
      setRenewals([]);
      setFilteredRenewals([]);
      setPageOfItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRenewalWindowConfig = async () => {
    try {
      const response = await renewalService.GetRenewalWindowWeeks();
      if (response.success && response.data) {
        setRenewalWindowWeeks(response.data.RenewalWindowWeeks || 8);
      }
    } catch (error) {
      console.error('Error loading renewal window config:', error);
    }
  };

  const handleProcessRenewal = (renewal) => {
    setSelectedRenewal(renewal);
    setProcessRenewalDialogOpen(true);
  };

  const handleCancelRenewal = (renewal) => {
    setSelectedRenewal(renewal);
    setCancelDialogOpen(true);
  };

  const handleBulkProcess = () => {
    if (selectedRenewals.length === 0) {
      toast.warning('Please select at least one renewal to process');
      return;
    }
    setBulkActionDialogOpen(true);
  };

  const applyFilters = (statusFilter, sortField, order, search = '') => {
    let filtered = [...renewals];

    // Apply search filter
    if (search && search.trim()) {
      const searchLower = search.trim().toLowerCase();
      filtered = filtered.filter(r => 
        (r.CustomerName && r.CustomerName.toLowerCase().includes(searchLower)) ||
        (r.Products && r.Products.some(p => p.toLowerCase().includes(searchLower))) ||
        (r.RenewalStatus && r.RenewalStatus.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(r => {
        if (statusFilter === 'Past Due') return r.IsPastDue;
        if (statusFilter === 'Grace Period') return r.IsInGracePeriod;
        return r.RenewalStatus === statusFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'renewalDate':
          aVal = new Date(a.RenewalDate);
          bVal = new Date(b.RenewalDate);
          break;
        case 'customerName':
          aVal = (a.CustomerName || '').toLowerCase();
          bVal = (b.CustomerName || '').toLowerCase();
          break;
        case 'daysUntilRenewal':
          aVal = a.DaysUntilRenewal;
          bVal = b.DaysUntilRenewal;
          break;
        case 'totalAmount':
          aVal = (a.Year1Cost || 0) + (a.Year2Cost || 0) + (a.Year3Cost || 0);
          bVal = (b.Year1Cost || 0) + (b.Year2Cost || 0) + (b.Year3Cost || 0);
          break;
        default:
          return 0;
      }

      if (order === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredRenewals(filtered);
    setIsChanged(!isChanged); // Trigger pagination update
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
    applyFilters(filterStatus, field, newOrder, searchTerm);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    applyFilters(filterStatus, sortBy, sortOrder, value);
  };

  const onChangePage = (pageItems) => {
    setPageOfItems(pageItems);
  };

  const getSummaryStats = () => {
    const stats = {
      total: filteredRenewals.length,
      unprocessed: filteredRenewals.filter(r => r.RenewalStatus === 'Unprocessed').length,
      invoiceGenerated: filteredRenewals.filter(r => r.RenewalStatus === 'Invoice Generated').length,
      pastDue: filteredRenewals.filter(r => r.IsPastDue).length,
      gracePeriod: filteredRenewals.filter(r => r.IsInGracePeriod).length,
      totalValue: filteredRenewals.reduce((sum, r) => sum + (r.Year1Cost || 0) + (r.Year2Cost || 0) + (r.Year3Cost || 0), 0),
    };
    return stats;
  };

  const exportToCSV = () => {
    if (filteredRenewals.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = ['Customer Name', 'Type', 'Products', 'Year 1 Cost', 'Year 2 Cost', 'Year 3 Cost', 'Renewal Date', 'Days Until', 'Status', 'Term'];
    const rows = filteredRenewals.map(r => [
      r.CustomerName || '',
      r.IsTrustLevel ? 'Trust' : 'School',
      (r.Products || []).join('; '),
      r.Year1Cost || 0,
      r.Year2Cost || 0,
      r.Year3Cost || 0,
      format(parseISO(r.RenewalDate), 'yyyy-MM-dd'),
      r.DaysUntilRenewal,
      r.RenewalStatus || 'Unprocessed',
      r.CurrentYearNumber ? `Year ${r.CurrentYearNumber} of ${r.SubscriptionTerm}` : `${r.SubscriptionTerm} Year(s)`,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `upcoming-renewals-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV exported successfully');
  };

  // Purple-header sort cell — click toggles the same sort handlers as before; the arrow shows
  // the active column and direction.
  const sortHeader = (label, field) => (
    <HeaderSort type='button' onClick={() => handleSort(field)}>
      {label}
      {sortBy === field && <SortArrow>{sortOrder === 'asc' ? '↑' : '↓'}</SortArrow>}
    </HeaderSort>
  );

  const daysTone = (r) =>
    r.IsPastDue ? TONE.danger : r.DaysUntilRenewal < 14 ? TONE.warning : TONE.neutral;

  const statusOptions = [
    { value: 'All', label: 'All' },
    { value: 'Unprocessed', label: 'Unprocessed' },
    { value: 'Invoice Generated', label: 'Invoice Generated' },
    { value: 'Past Due', label: 'Past Due' },
    { value: 'Grace Period', label: 'Grace Period' },
  ];

  const sortOptions = [
    { value: 'renewalDate', label: 'Renewal Date' },
    { value: 'customerName', label: 'Customer Name' },
    { value: 'daysUntilRenewal', label: 'Days Until Renewal' },
    { value: 'totalAmount', label: 'Total Amount' },
  ];

  const pageAllSelected =
    selectedRenewals.length === pageOfItems.length &&
    pageOfItems.length > 0 &&
    pageOfItems.every((r) => selectedRenewals.includes(r.SubscriptionId));
  const pagePartiallySelected =
    selectedRenewals.length > 0 &&
    selectedRenewals.length <
      pageOfItems.filter((r) => selectedRenewals.includes(r.SubscriptionId)).length;

  const toggleSelectAll = (checked) => {
    const pageIds = pageOfItems.map((r) => r.SubscriptionId);
    if (checked) {
      setSelectedRenewals([...new Set([...selectedRenewals, ...pageIds])]);
    } else {
      setSelectedRenewals(selectedRenewals.filter((id) => !pageIds.includes(id)));
    }
  };

  const toggleSelectRow = (id, checked) => {
    if (checked) {
      setSelectedRenewals([...selectedRenewals, id]);
    } else {
      setSelectedRenewals(selectedRenewals.filter((sid) => sid !== id));
    }
  };

  const renewalColumns = [
    {
      key: 'select',
      width: '5%',
      align: 'center',
      header: (
        <Checkbox
          size='small'
          sx={{
            p: 0,
            color: sa.color.white,
            '&.Mui-checked': { color: sa.color.white },
            '&.MuiCheckbox-indeterminate': { color: sa.color.white },
          }}
          checked={pageAllSelected}
          indeterminate={pagePartiallySelected}
          onChange={(e) => toggleSelectAll(e.target.checked)}
        />
      ),
      render: (r) => (
        <Checkbox
          size='small'
          sx={{ p: 0, color: sa.color.teal, '&.Mui-checked': { color: sa.color.teal } }}
          checked={selectedRenewals.includes(r.SubscriptionId)}
          onChange={(e) => toggleSelectRow(r.SubscriptionId, e.target.checked)}
        />
      ),
    },
    {
      key: 'customer',
      width: '15%',
      header: sortHeader('Customer', 'customerName'),
      render: (r) => <RowName>{r.CustomerName}</RowName>,
    },
    {
      key: 'type',
      width: '10%',
      header: 'Type',
      render: (r) => {
        const tone = r.IsTrustLevel ? TONE.trust : TONE.school;
        return (
          <Pill $bg={tone.bg} $fg={tone.fg}>
            {r.IsTrustLevel ? 'Trust' : 'School'}
          </Pill>
        );
      },
    },
    {
      key: 'products',
      width: '15%',
      header: 'Products',
      render: (r) => r.Products?.join(', ') || 'N/A',
    },
    {
      key: 'pricing',
      width: '10%',
      header: sortHeader('Current Pricing', 'totalAmount'),
      cellStyle: { whiteSpace: 'normal' },
      render: (r) => (
        <StackedCell>
          <span>Year 1: £{r.Year1Cost.toFixed(2)}</span>
          {r.SubscriptionTerm > 1 && <span>Year 2: £{r.Year2Cost.toFixed(2)}</span>}
          {r.SubscriptionTerm > 2 && <span>Year 3: £{r.Year3Cost.toFixed(2)}</span>}
        </StackedCell>
      ),
    },
    {
      key: 'renewalDate',
      width: '15%',
      header: sortHeader('Renewal Date', 'renewalDate'),
      cellStyle: { whiteSpace: 'normal' },
      render: (r) => (
        <StackedCell>
          <span>{format(parseISO(r.RenewalDate), 'MMM dd, yyyy')}</span>
          {r.IsLegacyCustomer && (
            <span>
              <Pill $bg={TONE.warning.bg} $fg={TONE.warning.fg}>
                Legacy
              </Pill>
            </span>
          )}
        </StackedCell>
      ),
    },
    {
      key: 'term',
      width: '9%',
      header: 'Term',
      render: (r) =>
        r.CurrentYearNumber
          ? `Year ${r.CurrentYearNumber} of ${r.SubscriptionTerm}`
          : `${r.SubscriptionTerm} Year${r.SubscriptionTerm > 1 ? 's' : ''}`,
    },
    {
      key: 'daysUntil',
      width: '8%',
      header: sortHeader('Days Until', 'daysUntilRenewal'),
      render: (r) => {
        const tone = daysTone(r);
        return (
          <Pill $bg={tone.bg} $fg={tone.fg}>
            {r.DaysUntilRenewal > 0 ? `${r.DaysUntilRenewal} days` : 'Overdue'}
          </Pill>
        );
      },
    },
    {
      key: 'status',
      width: '11%',
      header: 'Status',
      cellStyle: { whiteSpace: 'normal' },
      render: (r) => {
        const tone = r.RenewalStatus === 'Unprocessed' ? TONE.neutral : TONE.success;
        return (
          <StackedCell>
            <span>
              <Pill $bg={tone.bg} $fg={tone.fg}>
                {r.RenewalStatus}
              </Pill>
            </span>
            {r.IsInGracePeriod && (
              <span>
                <Pill $bg={TONE.warning.bg} $fg={TONE.warning.fg}>
                  Grace Period
                </Pill>
              </span>
            )}
          </StackedCell>
        );
      },
    },
    {
      key: 'actions',
      width: '17%',      
      header: 'Actions',
      render: (r) => (
        <ActionCell>
          <PillButton type='button' onClick={() => handleProcessRenewal(r)}>
            Process
          </PillButton>
          <PillButton type='button' $variant='outline' onClick={() => handleCancelRenewal(r)}>
            Cancel
          </PillButton>
        </ActionCell>
      ),
    },
  ];

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <Page>
            <SectionHeaderRow title='Upcoming Renewals' size='lg'>
              <PillButton
                type='button'
                onClick={handleBulkProcess}
                disabled={selectedRenewals.length === 0}
              >
                Bulk Process ({selectedRenewals.length})
              </PillButton>
              <PillButton
                type='button'
                $variant='outline'
                onClick={exportToCSV}
                disabled={filteredRenewals.length === 0}
              >
                <FileDownloadIcon fontSize='small' /> Export CSV
              </PillButton>
              <PillButton
                type='button'
                $variant='outline'
                onClick={() => setRenewalWindowConfigDialogOpen(true)}
              >
                <SettingsIcon fontSize='small' /> Configure Window
              </PillButton>
              <PillButton type='button' onClick={loadRenewals}>
                <RefreshIcon fontSize='small' /> Refresh
              </PillButton>
            </SectionHeaderRow>

            {!loading &&
              filteredRenewals.length > 0 &&
              (() => {
                const stats = getSummaryStats();
                return (
                  <StatGrid>
                    <StatCard $accent={STAT_ACCENT.total}>
                      <StatValue $color={STAT_ACCENT.total}>{stats.total}</StatValue>
                      <StatLabel>Total Renewals</StatLabel>
                    </StatCard>
                    <StatCard $accent={STAT_ACCENT.unprocessed}>
                      <StatValue $color={STAT_ACCENT.unprocessed}>{stats.unprocessed}</StatValue>
                      <StatLabel>Unprocessed</StatLabel>
                    </StatCard>
                    <StatCard $accent={STAT_ACCENT.invoiceGenerated}>
                      <StatValue $color={STAT_ACCENT.invoiceGenerated}>
                        {stats.invoiceGenerated}
                      </StatValue>
                      <StatLabel>Invoice Generated</StatLabel>
                    </StatCard>
                    <StatCard $accent={STAT_ACCENT.pastDue}>
                      <StatValue $color={STAT_ACCENT.pastDue}>{stats.pastDue}</StatValue>
                      <StatLabel>Past Due</StatLabel>
                    </StatCard>
                    <StatCard $accent={STAT_ACCENT.gracePeriod}>
                      <StatValue $color={STAT_ACCENT.gracePeriod}>{stats.gracePeriod}</StatValue>
                      <StatLabel>Grace Period</StatLabel>
                    </StatCard>
                    <StatCard $accent={STAT_ACCENT.totalValue}>
                      <StatValue $color={STAT_ACCENT.totalValue}>
                        £{stats.totalValue.toFixed(2)}
                      </StatValue>
                      <StatLabel>Total Value</StatLabel>
                    </StatCard>
                  </StatGrid>
                );
              })()}

            <Card $pad='16px'>
              <FilterBar>
                <SearchBox>
                  <SearchIcon />
                  <input
                    placeholder='Search by customer, product, or status...'
                    value={searchTerm}
                    onChange={handleSearchChange}
                    aria-label='Search renewals'
                  />
                </SearchBox>
                <FilterField>
                  Status
                  <SelectPill
                    value={filterStatus}
                    onChange={(v) => {
                      setFilterStatus(v);
                      applyFilters(v, sortBy, sortOrder, searchTerm);
                    }}
                    options={statusOptions}
                    ariaLabel='Filter by status'
                    size='sm'
                  />
                </FilterField>
                <FilterField>
                  Sort by
                  <SelectPill
                    value={sortBy}
                    onChange={(v) => {
                      setSortBy(v);
                      applyFilters(filterStatus, v, sortOrder, searchTerm);
                    }}
                    options={sortOptions}
                    ariaLabel='Sort by'
                    size='sm'
                  />
                </FilterField>
              </FilterBar>
              <SectionSubtitle>
                Showing {filteredRenewals.length} renewal(s) within {renewalWindowWeeks} weeks +
                overdue customers
              </SectionSubtitle>
            </Card>

            <SectionStates
              loading={loading}
              isEmpty={!loading && filteredRenewals.length === 0}
              emptyText='No upcoming renewals found'
            />

            {!loading && filteredRenewals.length > 0 && (
              <div>
                <DataTable
                  columns={renewalColumns}
                  rows={pageOfItems}
                  getRowKey={(r) => r.SubscriptionId}
                  empty='No upcoming renewals found'
                />
                <PaginationWrapper>
                  <Paging
                    items={filteredRenewals}
                    onChangePage={onChangePage}
                    isChanged={isChanged}
                  />
                </PaginationWrapper>
              </div>
            )}
          </Page>

          {/* Process Renewal Dialog */}
          <ProcessRenewalDialog
            open={processRenewalDialogOpen}
            onClose={() => setProcessRenewalDialogOpen(false)}
            renewal={selectedRenewal}
            onSuccess={loadRenewals}
          />

          {/* Cancel Subscription Dialog */}
          <CancelRenewalDialog
            open={cancelDialogOpen}
            onClose={() => setCancelDialogOpen(false)}
            renewal={selectedRenewal}
            onSuccess={loadRenewals}
          />

          {/* Bulk Action Dialog */}
          <BulkActionDialog
            open={bulkActionDialogOpen}
            onClose={() => setBulkActionDialogOpen(false)}
            selectedRenewalIds={selectedRenewals}
            renewals={renewals.filter(r => selectedRenewals.includes(r.SubscriptionId))}
            onSuccess={() => {
              setSelectedRenewals([]);
              loadRenewals();
            }}
          />

          {/* Renewal Window Configuration Dialog */}
          <RenewalWindowConfigDialog
            open={renewalWindowConfigDialogOpen}
            onClose={() => setRenewalWindowConfigDialogOpen(false)}
            currentWeeks={renewalWindowWeeks}
            onSuccess={(weeks) => {
              setRenewalWindowWeeks(weeks);
              loadRenewals();
            }}
          />
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}

// Process Renewal Dialog Component
function ProcessRenewalDialog({ open, onClose, renewal, onSuccess }) {
  const [formData, setFormData] = useState({
    products: [],
    productChargeability: {},
    year1Cost: 0,
    year2Cost: 0,
    year3Cost: 0,
    vatPercent: 20,
    discount: 0,
    startDate: null,
    endDate: null,
    subscriptionTerm: 1,
    newRenewalDate: null,
    isMultiYear: false,
    yearNumber: null,
  });
  const [loading, setLoading] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);

  const availableProducts = [
    'Main App & Toolkit',
    'Pupil Insights',
    'Parents Insights',
    'Staff Insights',
    'Edu Intelligence',
    'Pulse',
  ];

  const subscriptionTermOptions = [
    { value: 1, label: '1 Year' },
    { value: 2, label: '2 Years' },
    { value: 3, label: '3 Years' },
  ];

  useEffect(() => {
    if (renewal) {
      const currentDate = new Date();
      const renewalDate = parseISO(renewal.RenewalDate);
      const startDate = renewalDate > currentDate ? renewalDate : currentDate;
      const endDate = addYears(startDate, renewal.SubscriptionTerm);

      // Initialize product chargeability (default to true)
      const chargeability = {};
      (renewal.Products || []).forEach(product => {
        chargeability[product] = true;
      });

      setFormData({
        products: renewal.Products || [],
        productChargeability: chargeability,
        year1Cost: renewal.Year1Cost || 0,
        year2Cost: renewal.Year2Cost || 0,
        year3Cost: renewal.Year3Cost || 0,
        vatPercent: 20,
        discount: 0,
        startDate: startDate,
        endDate: endDate,
        subscriptionTerm: renewal.SubscriptionTerm || 1,
        newRenewalDate: null,
        isMultiYear: renewal.SubscriptionTerm > 1,
        yearNumber: renewal.CurrentYearNumber || (renewal.SubscriptionTerm > 1 ? 1 : null),
      });
      setGeneratedInvoice(null);
    }
  }, [renewal]);

  const handleProductChange = (event) => {
    const value = event.target.value;
    const newProducts = typeof value === 'string' ? value.split(',') : value;
    
    // Initialize chargeability for new products
    const newChargeability = { ...formData.productChargeability };
    newProducts.forEach(product => {
      if (!newChargeability.hasOwnProperty(product)) {
        newChargeability[product] = true;
      }
    });

    setFormData({
      ...formData,
      products: newProducts,
      productChargeability: newChargeability,
    });
  };

  const handleChargeabilityToggle = (productName) => {
    setFormData({
      ...formData,
      productChargeability: {
        ...formData.productChargeability,
        [productName]: !formData.productChargeability[productName],
      },
    });
  };

  const handleTermChange = (newTerm) => {
    const newEndDate = formData.startDate 
      ? addYears(new Date(formData.startDate), newTerm)
      : addYears(new Date(), newTerm);
    
    setFormData({
      ...formData,
      subscriptionTerm: newTerm,
      endDate: newEndDate,
      isMultiYear: newTerm > 1,
      yearNumber: newTerm > 1 ? (formData.yearNumber || 1) : null,
    });
  };

  const handleStartDateChange = (date) => {
    const newEndDate = date ? addYears(date, formData.subscriptionTerm) : formData.endDate;
    setFormData({
      ...formData,
      startDate: date,
      endDate: newEndDate,
    });
  };

  const calculateTotal = () => {
    const baseCost = parseFloat(formData.year1Cost || 0) + 
                     parseFloat(formData.year2Cost || 0) + 
                     parseFloat(formData.year3Cost || 0);
    const afterDiscount = baseCost - parseFloat(formData.discount || 0);
    const vatAmount = afterDiscount * (parseFloat(formData.vatPercent || 20) / 100);
    return afterDiscount + vatAmount;
  };

  const handleSubmit = async () => {
    if (!formData.startDate || !formData.endDate) {
      toast.error('Please select start and end dates');
      return;
    }

    if (formData.products.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    setLoading(true);
    try {
      // Map products to ProductDto format with chargeability
      const products = formData.products.map(productName => ({
        ProductName: productName,
        IsChargeable: formData.productChargeability[productName] !== false,
      }));
      
      const renewalData = {
        SubscriptionId: renewal.SubscriptionId,
        Products: products,
        Year1Cost: parseFloat(formData.year1Cost || 0),
        Year2Cost: parseFloat(formData.year2Cost || 0),
        Year3Cost: parseFloat(formData.year3Cost || 0),
        VatPercentApplied: parseFloat(formData.vatPercent || 20),
        Discount: parseFloat(formData.discount || 0),
        StartDate: formData.startDate.toISOString(),
        EndDate: formData.endDate.toISOString(),
        SubscriptionTerm: formData.subscriptionTerm,
        NewRenewalDate: formData.newRenewalDate ? formData.newRenewalDate.toISOString() : null,
        IsMultiYear: formData.isMultiYear,
        YearNumber: formData.yearNumber,
      };
      
      const response = await renewalService.ProcessRenewal(renewalData);
      if (response.success) {
        setGeneratedInvoice(response.data);
        toast.success('Renewal processed successfully. Invoice generated.');
      } else {
        toast.error(response.message || 'Failed to process renewal');
      }
    } catch (error) {
      console.error('Error processing renewal:', error);
      toast.error('Failed to process renewal');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (generatedInvoice) {
      onSuccess();
    }
    setGeneratedInvoice(null);
    onClose();
  };

  const downloadInvoice = (invoiceId) => {
    const url = invoiceManagementAPI.DownloadInvoicePdf(invoiceId);
    window.open(url, '_blank');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Process Renewal - {renewal?.CustomerName}
        {renewal?.CurrentYearNumber && renewal.SubscriptionTerm > 1 && (
          <Chip 
            label={`Multi-Year Continuation - Year ${renewal.CurrentYearNumber} of ${renewal.SubscriptionTerm}`}
            size="small"
            color="primary"
            sx={{ ml: 2 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {generatedInvoice ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Invoice Generated Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Invoice #{generatedInvoice.InvoiceNumber || generatedInvoice.Id} has been created.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={() => downloadInvoice(generatedInvoice.Id)}
              sx={{ mt: 2 }}
            >
              Download Invoice PDF
            </Button>
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Review and Edit Subscription Terms
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {/* Products Section */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Products</InputLabel>
                  <Select
                    multiple
                    value={formData.products}
                    onChange={handleProductChange}
                    input={<OutlinedInput label="Products" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {availableProducts.map((product) => (
                      <MenuItem key={product} value={product}>
                        {product}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Product Chargeability */}
              {formData.products.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Product Chargeability
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {formData.products.map((product) => (
                      <FormControlLabel
                        key={product}
                        control={
                          <Switch
                            checked={formData.productChargeability[product] !== false}
                            onChange={() => handleChargeabilityToggle(product)}
                          />
                        }
                        label={product}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Pricing Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Pricing
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Year 1 Cost (£)"
                  type="number"
                  value={formData.year1Cost}
                  onChange={(e) => setFormData({ ...formData, year1Cost: e.target.value })}
                  InputProps={{ startAdornment: '£' }}
                />
              </Grid>
              {formData.subscriptionTerm > 1 && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Year 2 Cost (£)"
                    type="number"
                    value={formData.year2Cost}
                    onChange={(e) => setFormData({ ...formData, year2Cost: e.target.value })}
                    InputProps={{ startAdornment: '£' }}
                  />
                </Grid>
              )}
              {formData.subscriptionTerm > 2 && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Year 3 Cost (£)"
                    type="number"
                    value={formData.year3Cost}
                    onChange={(e) => setFormData({ ...formData, year3Cost: e.target.value })}
                    InputProps={{ startAdornment: '£' }}
                  />
                </Grid>
              )}

              {/* VAT and Discount */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="VAT Percentage (%)"
                  type="number"
                  value={formData.vatPercent}
                  onChange={(e) => setFormData({ ...formData, vatPercent: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount (£)"
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  InputProps={{ startAdornment: '£' }}
                />
              </Grid>

              {/* Total Display */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="h6">
                    Total (incl. VAT): £{calculateTotal().toFixed(2)}
                  </Typography>
                </Box>
              </Grid>

              {/* Subscription Term */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Term</InputLabel>
                  <Select
                    value={formData.subscriptionTerm}
                    onChange={(e) => handleTermChange(e.target.value)}
                    label="Subscription Term"
                  >
                    {subscriptionTermOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Multi-Year Year Number */}
              {formData.isMultiYear && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current Year Number"
                    type="number"
                    value={formData.yearNumber || 1}
                    onChange={(e) => setFormData({ ...formData, yearNumber: parseInt(e.target.value) })}
                    inputProps={{ min: 1, max: formData.subscriptionTerm }}
                  />
                </Grid>
              )}

              {/* Dates Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Subscription Dates
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(date) => setFormData({ ...formData, endDate: date })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              {/* New Renewal Date (Optional) */}
              <Grid item xs={12}>
                <DatePicker
                  label="New Renewal Date (Optional - for next period)"
                  value={formData.newRenewalDate}
                  onChange={(date) => setFormData({ ...formData, newRenewalDate: date })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {generatedInvoice ? 'Close' : 'Cancel'}
        </Button>
        {!generatedInvoice && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Generate Invoice'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Bulk Action Dialog Component
function BulkActionDialog({ open, onClose, selectedRenewalIds, renewals, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleBulkProcess = async () => {
    if (selectedRenewalIds.length === 0) {
      toast.error('No renewals selected');
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      // For bulk processing, we'll process each renewal with default values
      // In a real scenario, you might want a bulk edit form, but for now we'll use current values
      const renewalDataList = renewals.map(renewal => {
        const products = (renewal.Products || []).map(p => ({
          ProductName: typeof p === 'string' ? p : p.ProductName,
          IsChargeable: true,
        }));

        const currentDate = new Date();
        const renewalDate = parseISO(renewal.RenewalDate);
        const startDate = renewalDate > currentDate ? renewalDate : currentDate;
        const endDate = addYears(startDate, renewal.SubscriptionTerm);

        return {
          SubscriptionId: renewal.SubscriptionId,
          Products: products,
          Year1Cost: renewal.Year1Cost || 0,
          Year2Cost: renewal.Year2Cost || 0,
          Year3Cost: renewal.Year3Cost || 0,
          VatPercentApplied: 20,
          Discount: 0,
          StartDate: startDate.toISOString(),
          EndDate: endDate.toISOString(),
          SubscriptionTerm: renewal.SubscriptionTerm || 1,
          NewRenewalDate: null,
          IsMultiYear: (renewal.SubscriptionTerm || 1) > 1,
          YearNumber: renewal.CurrentYearNumber || ((renewal.SubscriptionTerm || 1) > 1 ? 1 : null),
        };
      });

      const response = await renewalService.ProcessBulkRenewals(renewalDataList);
      
      if (response.success) {
        setResults({
          success: response.data.SuccessCount || 0,
          failure: response.data.FailureCount || 0,
          invoiceIds: response.data.GeneratedInvoiceIds || [],
          errors: response.data.Errors || [],
        });
        toast.success(`Processed ${response.data.SuccessCount || 0} renewals successfully`);
      } else {
        toast.error(response.message || 'Failed to process bulk renewals');
      }
    } catch (error) {
      console.error('Error processing bulk renewals:', error);
      toast.error('Failed to process bulk renewals');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBulkInvoices = async () => {
    if (!results || !results.invoiceIds || results.invoiceIds.length === 0) {
      toast.error('No invoices to download');
      return;
    }

    try {
      // Download invoices one by one (in a real scenario, you might want bulk ZIP download)
      for (const invoiceId of results.invoiceIds) {
        setTimeout(() => {
          const url = invoiceManagementAPI.DownloadInvoicePdf(invoiceId);
          window.open(url, '_blank');
        }, 500);
      }
      toast.success(`Opening ${results.invoiceIds.length} invoice(s) for download`);
    } catch (error) {
      console.error('Error downloading invoices:', error);
      toast.error('Failed to download invoices');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Renewal Processing</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          Process {selectedRenewalIds.length} selected renewal(s) with current subscription terms?
        </Typography>
        {renewals.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Renewals:
            </Typography>
            <List dense>
              {renewals.map((renewal) => (
                <ListItem key={renewal.SubscriptionId}>
                  <ListItemText
                    primary={renewal.CustomerName}
                    secondary={`Renewal Date: ${format(parseISO(renewal.RenewalDate), 'MMM dd, yyyy')}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {results && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Processing Results
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="h4">{results.success}</Typography>
                  <Typography variant="body2">Successful</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="h4">{results.failure}</Typography>
                  <Typography variant="body2">Failed</Typography>
                </Box>
              </Grid>
            </Grid>

            {results.errors && results.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error">
                  Errors:
                </Typography>
                <List dense>
                  {results.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={error}
                        primaryTypographyProps={{ variant: 'body2', color: 'error' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {results.invoiceIds && results.invoiceIds.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadBulkInvoices}
                  fullWidth
                >
                  Download All Invoices ({results.invoiceIds.length})
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {results ? 'Close' : 'Cancel'}
        </Button>
        {!results && (
          <Button
            onClick={handleBulkProcess}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Process All Renewals'}
          </Button>
        )}
        {results && results.invoiceIds && results.invoiceIds.length > 0 && (
          <Button
            onClick={() => {
              onSuccess();
              onClose();
            }}
            variant="contained"
            color="primary"
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Renewal Window Configuration Dialog
function RenewalWindowConfigDialog({ open, onClose, currentWeeks, onSuccess }) {
  const [weeks, setWeeks] = useState(currentWeeks);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setWeeks(currentWeeks);
  }, [currentWeeks]);

  const handleSave = async () => {
    if (weeks < 1 || weeks > 52) {
      toast.error('Renewal window must be between 1 and 52 weeks');
      return;
    }

    setLoading(true);
    try {
      const response = await renewalService.UpdateRenewalWindowWeeks(weeks);
      if (response.success) {
        toast.success(`Renewal window updated to ${weeks} weeks`);
        onSuccess(weeks);
        onClose();
      } else {
        toast.error(response.message || 'Failed to update renewal window');
      }
    } catch (error) {
      console.error('Error updating renewal window:', error);
      toast.error('Failed to update renewal window');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure Renewal Window</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Set the number of weeks before renewal date to show customers in Upcoming Renewals.
          Past-due customers will always be included regardless of this setting.
        </Typography>
        <TextField
          fullWidth
          label="Renewal Window (Weeks)"
          type="number"
          value={weeks}
          onChange={(e) => setWeeks(parseInt(e.target.value) || 8)}
          inputProps={{ min: 1, max: 52 }}
          sx={{ mt: 3 }}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          Current setting: {currentWeeks} weeks. Recommended: 8 weeks.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || weeks < 1 || weeks > 52}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Cancel Renewal Dialog Component
function CancelRenewalDialog({ open, onClose, renewal, onSuccess }) {
  const [reason, setReason] = useState('');

  const handleCancel = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    try {
      const response = await renewalService.CancelSubscriptionFromRenewal(renewal.SubscriptionId, reason);
      if (response.success) {
        toast.success('Subscription cancelled successfully');
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Cancel Subscription - {renewal?.CustomerName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Are you sure you want to cancel this subscription? This action will:
        </Typography>
        <ul>
          <li>Mark the subscription as Cancelled</li>
          <li>NOT generate a renewal invoice</li>
          <li>Remove the customer from Upcoming Renewals</li>
          <li>Begin a 2-month grace period</li>
        </ul>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Cancellation Reason (Required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 2 }}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>No, Keep Subscription</Button>
        <Button onClick={handleCancel} variant="contained" color="error" disabled={!reason.trim()}>
          Yes, Cancel Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withRouter(UpcomingRenewals);
